import ImageEditor, {
  BackgroundColorSelector,
  getEditorPreset,
  useEditorReducer,
} from '@components/BlinqImageEditor';
import { colorHexToNumber, numberToColorHex } from '@components/BlinqImageEditor/helpers';
import Button from '@components/Button/Button';
import EditCardDialogPopover from '@components/Card/EditCardDialogPopover/EditCardDialogPopover';
import EditCardPopover, {
  EditCardControlsSection,
  Panel,
  StackedPanels,
} from '@components/Card/EditCardPopover/EditCardPopover';
import ImageSelector from '@components/ImageSelector/ImageSelector';
import {
  ButtonsSection,
  ContentSection,
  Scrollable,
  StickyBottom,
  TitleSection,
} from '@components/Popover/PopoverLayouts';
import { cardContext } from '@context/card-context';
import { CardEditPopoverShellContext } from '@context/card-edit-popover-shell-context';
import { cardPresetContext } from '@context/card-preset-context';
import { notificationContext } from '@context/notifications';
import { useRemoteConfigNumber } from '@hooks/use-remote-config-number';
import { analytics } from '@modules/analytics';
import { getImageDimensions, uploadImageAsync } from '@modules/image-helpers';
import type { PositionerOptions } from '@modules/popover-helpers';
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { CardImage } from 'types';

import PreventOverrideToggle from '../EditCardPopover/PreventOverrideToggle';
import LinkStatusContainer from '../FieldEditPopovers/LinkStatusContainer/LinkStatusContainer';
import styles from './CardHeader.module.scss';
import { type ImageType, typeToFieldId } from './utilities';

const imageEditorCanvasHeight = 190;

function maxDimensionForType(imageType: ImageType): number {
  switch (imageType) {
    case 'profile':
      return 1200;
    case 'logo':
      return 1200;
    case 'cover':
      return 1500;
    default:
      return 1500;
  }
}

/** Simple function to return a more readable string.
 * When src is falsy, we are adding, otherwise editing
 * Transforms the short type name to the human friendly name
 */
function friendlyPopoverTitle(type: ImageType, src: string | undefined): string {
  const result = {
    firstWord: src ? 'Edit' : 'Add',
    secondWord: 'image',
  };
  switch (type) {
    case 'cover': {
      result.secondWord = 'cover photo';
      break;
    }
    case 'logo': {
      result.secondWord = 'company logo';
      break;
    }
    case 'profile': {
      result.secondWord = 'profile picture';
      break;
    }
  }
  return `${result.firstWord} ${result.secondWord}`;
}

interface PicturePopoverCallbacks {
  onClose: () => void;
  onUpdate: (imageReference: CardImage, quality?: 'high' | 'low') => void;
  onSelect: (imageReference: CardImage) => void;
  onSelectOpen?: (imageType: ImageType) => void;
  onRemove: () => void;
  onSave: (imageUrl: CardImage) => void;
  onUnlink: () => void;
}

export interface PicturePopoverProps extends PicturePopoverCallbacks {
  canLock: boolean;
  imageReference: CardImage | undefined;
  isPreset: boolean;
  presetId: string | undefined;
  cardId: string | undefined;
  userId: string;
  orgId: string;
  linked: boolean;
  positionerOptions?: PositionerOptions;
  show: boolean;
  targetNode: HTMLElement | undefined;
  type: ImageType;
}

interface EditPicturePopoverHookParameters {
  type: ImageType;
  imageReference: CardImage | undefined;
  show: boolean;
  linked: boolean;
  isPreset: boolean;
  presetId: string | undefined;
  userId: string;
  orgId: string;
  cardId: string | undefined;
}
const useEditPicturePopover = (
  { type, imageReference, show, linked, isPreset }: EditPicturePopoverHookParameters,
  {
    onClose,
    onUpdate,
    onSelect,
    onSelectOpen,
    onRemove,
    onSave,
    onUnlink,
  }: PicturePopoverCallbacks,
) => {
  const context_preset = useContext(cardPresetContext);
  const context_card = useContext(cardContext);
  const {
    canLock,
    lockedFields,
    layoutImagesLocked,
    rollbackChanges,
    saveChanges,
    toggleFieldLock,
  } = isPreset ? context_preset : context_card;
  const { notify } = useContext(notificationContext);
  const [showDeleteWarning, setShowDeleteWarning] = useState(false);
  const [showUnlinkWarning, setShowUnlinkWarning] = useState(false);
  const [editedImageReference, setEditedImageReference] = useState<CardImage | undefined>(
    imageReference,
  );

  const [fieldsChanged, setFieldsChanged] = useState(false);
  const [saving, setSaving] = useState(false);
  const originalLockStateRef = useRef<{ isLocked: boolean }>(undefined);

  const hasInitialisedRef = useRef(false);

  const isLocked = lockedFields.includes(typeToFieldId(type)) || layoutImagesLocked;

  const smallestDimension = useRemoteConfigNumber('min_card_img_dimension', 200);
  const defaultEditorState = useMemo(() => {
    return {
      id: imageReference?.originalImageUrl || '',
      zoom: imageReference?.editorMetadata?.zoom,
      backgroundColor: imageReference?.editorMetadata?.backgroundColor
        ? colorHexToNumber(imageReference?.editorMetadata?.backgroundColor)
        : null,
      position: imageReference?.editorMetadata?.imgPosition || { x: 0, y: 0 }, // origin
    };
  }, [
    imageReference?.originalImageUrl,
    imageReference?.editorMetadata?.zoom,
    imageReference?.editorMetadata?.backgroundColor,
    imageReference?.editorMetadata?.imgPosition,
  ]);
  const [editorState, updateEditorState] = useEditorReducer(defaultEditorState);
  useEffect(() => {
    if (defaultEditorState) {
      updateEditorState({ type: 'RESET', state: defaultEditorState });
    }
  }, [defaultEditorState, updateEditorState]);

  useEffect(() => {
    if (!hasInitialisedRef.current) {
      hasInitialisedRef.current = true;
      return;
    }

    if (!show) {
      setEditedImageReference(undefined);
      setShowUnlinkWarning(false);
      setShowDeleteWarning(false);
      setFieldsChanged(false);
    }
  }, [show]);

  useEffect(() => {
    if (show && originalLockStateRef.current === undefined) {
      originalLockStateRef.current = { isLocked };
    } else if (!show) {
      originalLockStateRef.current = undefined;
    }
  }, [show, isLocked]);

  // Snapshot the current cardFields when the popover opens, so handleCancel
  // can roll back to that exact point. Without this, rollback uses whatever
  // global bookmark happens to be set (initial mount, brand-color change,
  // or the previous popover's save), which can mismatch when the user has
  // already saved this image and then re-opens to edit it — rolling back
  // would otherwise drop the saved image because the bookmark didn't get
  // refreshed between the previous save's setTimeout(0) and the next open.
  const prevShowRef = useRef(false);
  useEffect(() => {
    if (show && !prevShowRef.current) {
      saveChanges();
    }
    prevShowRef.current = show;
  }, [show, saveChanges]);

  function handleDelete() {
    analytics.track({
      name: 'removepicture_confirmed',
      properties: { module: 'card_designer', card_picture_type: type },
    });
    onRemove();
    updateEditorState({ type: 'RESET' });
    saveChanges(false);
    if (isLocked) {
      toggleFieldLock(type, false);
    }
    onClose();
  }

  function handleCancel() {
    analytics.track({ name: 'canceled', properties: { module: 'image_editor' } });
    updateEditorState({ type: 'RESET', state: defaultEditorState });
    rollbackChanges();
    toggleFieldLock(type, originalLockStateRef.current?.isLocked);
    onClose();
  }

  function handleImageSelect(imageReference: CardImage) {
    if (imageReference.originalImageUrl) {
      analytics.track({
        name: 'addpicture_selected',
        properties: {
          module: 'card_designer',
          picture_select_method: 'choose_from_library',
          card_picture_type: type,
        },
      });
      getImageDimensions(imageReference.originalImageUrl).then((dimensions) => {
        if (dimensions[0] < smallestDimension || dimensions[1] < smallestDimension) {
          notify({
            title: 'Small image',
            type: 'warning',
            message: 'This image is smaller than recommended',
            autoHideAfterTimeout: true,
            timeout: 5000,
            size: 'small',
          });
        }
      });
    }
    onSelect(imageReference);
    setFieldsChanged(true);
  }

  useEffect(() => {
    if (
      show &&
      imageReference?.editedImageUrl === undefined &&
      imageReference?.editorMetadata === undefined
    ) {
      onSelectOpen?.(type);
    }
  }, [show, imageReference, type, onSelectOpen]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: historic
  const handleLowQualityImageUpdate = useCallback(
    (editedImageUrl?: string | undefined) => {
      if (imageReference && editedImageUrl) {
        const newImageRef = {
          ...imageReference,
          editedImageUrl,
          editorMetadata: {
            ...imageReference.editorMetadata,
            editorVersion: editorState.editorVersion,
          },
        };
        setEditedImageReference(newImageRef);
        onUpdate(newImageRef, 'low');
        setFieldsChanged(true);
      }
    },
    [
      onUpdate,
      setFieldsChanged,
      setEditedImageReference,
      imageReference,
      editorState.editorVersion,
    ],
  );
  // biome-ignore lint/correctness/useExhaustiveDependencies: historic
  const handleHighQualityImageUpdate = useCallback(
    (editedImageUrl?: string | undefined) => {
      if (imageReference && editedImageUrl) {
        const newImageRef = {
          ...imageReference,
          editedImageUrl,
          editorMetadata: {
            ...imageReference.editorMetadata,
            editorVersion: editorState.editorVersion,
          },
        };
        setEditedImageReference(newImageRef);
        onUpdate(newImageRef, 'high');
        setFieldsChanged(true);
      }
    },
    [
      onUpdate,
      setFieldsChanged,
      setEditedImageReference,
      imageReference,
      editorState.editorVersion,
    ],
  );

  function handleClosePopover() {
    updateEditorState({ type: 'RESET', state: defaultEditorState });
    rollbackChanges();
    toggleFieldLock(type, originalLockStateRef.current?.isLocked);
    onClose();
  }

  function handleSaveError(e: unknown) {
    console.error(e);
    notify({
      type: 'warning',
      timeout: 8000,
      title: 'Image upload failed',
      message:
        'We were not able to upload and save your image. Please check your internet connection and try again in a few minutes.',
    });

    setSaving(false);
  }

  function handleSaveSuccess(
    editedImageUrl: string,
    originalImageUrl: string,
    reference: CardImage,
  ) {
    onSave({
      editedImageUrl,
      originalImageUrl,
      editorMetadata: {
        imgPosition: editorState.position
          ? { x: editorState.position?.x, y: editorState.position?.y }
          : null,
        backgroundColor:
          editorState.backgroundColor !== null && editorState.backgroundColor !== undefined
            ? numberToColorHex(editorState.backgroundColor)
            : undefined,
        zoom: editorState.zoom || 1,
        editorVersion: editorState.editorVersion,
        createdAt: reference.editorMetadata?.createdAt,
      },
    });
    analytics.track({ name: 'imagesaved', properties: { module: 'image_editor' } });
    saveChanges();
    setSaving(false);
    onClose();
  }

  function performSave() {
    const reference = editedImageReference || imageReference; // take a local reference for closure.
    if (!reference) {
      // The lock state has change but there is no image, i.e. the user doesn't want an image to be added
      saveChanges();
      setSaving(false);
      onClose();
      return;
    }

    setSaving(true);

    // upload in parallel
    // todo: add upload progress indicators
    const editedPromise = uploadImageAsync(reference.editedImageUrl, {
      onProgressUpdate: undefined,
    });
    const originalPromise = uploadImageAsync(reference.originalImageUrl, {
      onProgressUpdate: undefined,
    });

    Promise.all([editedPromise, originalPromise])
      .then(([edited, original]) => {
        handleSaveSuccess(edited, original, reference);
      })
      .catch(handleSaveError);
  }

  function handleSave() {
    if (linked) {
      setShowUnlinkWarning(true);
    } else {
      performSave();
    }
  }

  function handleCancelDeleteWarning() {
    setShowDeleteWarning(false);
  }

  function handleDeletePress() {
    analytics.track({ name: 'removepicture_clicked', properties: { module: 'card_designer' } });
    setShowDeleteWarning(true);
  }

  function handleCancelUnlink() {
    setShowUnlinkWarning(false);
    handleClosePopover();
  }

  function handleConfirmUnlink() {
    if (type !== 'profile') {
      return;
    }

    onUnlink();
    performSave();
  }

  function handleToggleLock() {
    setFieldsChanged(true);
    toggleFieldLock(typeToFieldId(type));
  }

  return {
    canLock,
    editorState,
    fieldsChanged,
    isLocked,
    saving,
    setFieldsChanged,
    showDeleteWarning,
    showUnlinkWarning,
    updateEditorState,
    handlers: {
      handleDelete,
      handleCancel,
      handleImageSelect,
      handleLowQualityImageUpdate,
      handleHighQualityImageUpdate,
      handleClosePopover,
      handleSave,
      handleCancelDeleteWarning,
      handleDeletePress,
      handleToggleLock,
      handleCancelUnlink,
      handleConfirmUnlink,
    },
  };
};

export const EditPicturePopover: React.FC<React.PropsWithChildren<PicturePopoverProps>> = (
  props,
) => {
  const {
    targetNode,
    show,
    imageReference,
    type,
    linked,
    positionerOptions,
    isPreset,
    cardId,
    presetId,
    userId,
    orgId,
    ...callbacks
  } = props;

  const {
    canLock,
    editorState,
    fieldsChanged,
    handlers,
    isLocked,
    saving,
    showDeleteWarning,
    showUnlinkWarning,
    updateEditorState,
  } = useEditPicturePopover(
    { type, imageReference, show, linked, isPreset, presetId, cardId, userId, orgId },
    callbacks,
  );
  const {
    handleDelete,
    handleCancel,
    handleImageSelect,
    handleLowQualityImageUpdate,
    handleHighQualityImageUpdate,
    handleClosePopover,
    handleSave,
    handleCancelDeleteWarning,
    handleDeletePress,
    handleToggleLock,
    handleCancelUnlink,
    handleConfirmUnlink,
  } = handlers;

  const src = imageReference?.originalImageUrl;
  // if the image has no editor version, then we assume it is an old card, and therefore needs some zoom correction.
  const correctionCoefficient =
    imageReference?.editorMetadata?.editorVersion === undefined ? 0.98 : undefined;

  useEffect(() => {
    if (targetNode && show) {
      // choose behavior: auto so that the popover doesnt reposition awkwardly
      targetNode.scrollIntoView({ behavior: 'auto', block: 'center' });
    }
  }, [targetNode, show]);

  const { mode: shellMode } = useContext(CardEditPopoverShellContext);

  const shouldLock = isLocked && !canLock;
  if (shouldLock) {
    return null;
  }

  const pictureBody = (
    <div className={styles['edit-popover-content']} data-function={src ? 'editor' : 'selector'}>
      <LinkStatusContainer linked={linked} top={-5}>
        {!src && (
          <div style={{ height: '132px' }}>
            <ImageSelector
              maxDimension={maxDimensionForType(type)}
              minDimension={5}
              onSelect={(originalImageUrl) =>
                handleImageSelect({
                  originalImageUrl,
                  editorMetadata: { editorVersion: 'web_selector_v1' },
                })
              }
            />
          </div>
        )}
        {src && (
          <>
            <div className={styles['editor-container']}>
              <ImageEditor
                key={type}
                {...getEditorPreset(type)}
                canvasHeight={imageEditorCanvasHeight}
                focusOnMount={true}
                imgSrc={src}
                editorState={editorState}
                updateEditorState={updateEditorState}
                onHighQualityChanged={handleHighQualityImageUpdate}
                onLowQualityChanged={handleLowQualityImageUpdate}
                zoomCorrectionCoefficient={correctionCoefficient}
              />
            </div>
            <StackedPanels>
              {!getEditorPreset(type).disableBgEdit && !getEditorPreset(type).imgCover && (
                <Panel>
                  <BackgroundColorSelector
                    suggestedColors={editorState.suggestedbackgroundColors}
                    color={editorState.backgroundColor || 0}
                    setColor={(color) =>
                      updateEditorState({
                        type: 'SET_BG_COLOR',
                        color,
                        showMessage: true,
                      })
                    }
                    isCustomSelectOpen={!!editorState.showCustomBackgroundColorSelector}
                    setIsCustomSelectOpen={(value) => {
                      updateEditorState({
                        type: 'SHOW_CUSTOM_BG_SELECTOR',
                        value,
                      });
                    }}
                  />
                </Panel>
              )}

              {canLock && (
                <Panel variant="tight">
                  <PreventOverrideToggle isLocked={isLocked} onLockToggle={handleToggleLock} />
                </Panel>
              )}
            </StackedPanels>
          </>
        )}
      </LinkStatusContainer>
    </div>
  );

  if (shellMode === 'dialog') {
    return (
      <EditCardDialogPopover
        adaptive={false}
        preventCloseOnClickOutsidePopover={editorState.showCustomBackgroundColorSelector}
        targetNode={targetNode}
        show={show}
        onClose={handleClosePopover}
        drawFocus={fieldsChanged}
        title={friendlyPopoverTitle(type, src)}
        onCancel={handleCancel}
        onSubmit={src ? () => handleSave() : undefined}
        canSubmit={fieldsChanged}
        canDelete={!!imageReference && !!src}
        onDeletePress={handleDeletePress}
        onDelete={handleDelete}
        onCancelDeleteWarning={handleCancelDeleteWarning}
        showDeleteWarning={showDeleteWarning}
        showUnlinkWarning={showUnlinkWarning}
        onCancelUnlink={handleCancelUnlink}
        onConfirmUnlink={handleConfirmUnlink}
        unlinkWarningMessage="If you save the changes made to this profile picture than it will be unlinked"
        unlinking={saving}
        saving={saving}
        positionDeps={[src, editorState?.loaded]}
        positionerOptions={positionerOptions}
        fieldType="picture"
      >
        {pictureBody}
      </EditCardDialogPopover>
    );
  }

  return (
    <EditCardPopover
      adaptive={false}
      preventCloseOnClickOutsidePopover={editorState.showCustomBackgroundColorSelector}
      targetNode={targetNode}
      show={show}
      onClose={handleClosePopover}
      drawFocus={fieldsChanged}
      onCancelDeleteWarning={handleCancelDeleteWarning}
      showDeleteWarning={showDeleteWarning}
      onDelete={handleDelete}
      positionDeps={[src, editorState?.loaded]}
      onCancelUnlink={handleCancelUnlink}
      onConfirmUnlink={handleConfirmUnlink}
      positionerOptions={positionerOptions}
      showUnlinkWarning={showUnlinkWarning}
      unlinkWarningMessage="If you save the changes made to this profile picture than it will be unlinked"
      unlinking={saving}
      fieldType="picture"
    >
      <div className={styles['edit-popover-content']} data-function={src ? 'editor' : 'selector'}>
        <Scrollable>
          <TitleSection text={friendlyPopoverTitle(type, src)} onClose={handleClosePopover} />
          <ContentSection>
            <LinkStatusContainer linked={linked} top={-5}>
              {!src && (
                <div style={{ height: '132px' }}>
                  <ImageSelector
                    maxDimension={maxDimensionForType(type)}
                    minDimension={5}
                    onSelect={(originalImageUrl) =>
                      handleImageSelect({
                        originalImageUrl,
                        editorMetadata: { editorVersion: 'web_selector_v1' },
                      })
                    }
                  />
                </div>
              )}
              {src && (
                <>
                  <div className={styles['editor-container']}>
                    <ImageEditor
                      key={type}
                      {...getEditorPreset(type)}
                      canvasHeight={imageEditorCanvasHeight}
                      focusOnMount={true}
                      imgSrc={src}
                      editorState={editorState}
                      updateEditorState={updateEditorState}
                      onHighQualityChanged={handleHighQualityImageUpdate}
                      onLowQualityChanged={handleLowQualityImageUpdate}
                      zoomCorrectionCoefficient={correctionCoefficient}
                    />
                  </div>
                  <StackedPanels>
                    {!getEditorPreset(type).disableBgEdit && !getEditorPreset(type).imgCover && (
                      <Panel>
                        <BackgroundColorSelector
                          suggestedColors={editorState.suggestedbackgroundColors}
                          color={editorState.backgroundColor || 0}
                          setColor={(color) =>
                            updateEditorState({
                              type: 'SET_BG_COLOR',
                              color,
                              showMessage: true,
                            })
                          }
                          isCustomSelectOpen={!!editorState.showCustomBackgroundColorSelector}
                          setIsCustomSelectOpen={(value) => {
                            updateEditorState({
                              type: 'SHOW_CUSTOM_BG_SELECTOR',
                              value,
                            });
                          }}
                        />
                      </Panel>
                    )}

                    {canLock && (
                      <Panel variant="tight">
                        <PreventOverrideToggle
                          isLocked={isLocked}
                          onLockToggle={handleToggleLock}
                        />
                      </Panel>
                    )}
                  </StackedPanels>
                </>
              )}
            </LinkStatusContainer>
          </ContentSection>
        </Scrollable>
        {src && (
          // dont show buttons unless we have an image already selected
          <StickyBottom>
            <ButtonsSection>
              <EditCardControlsSection
                canDelete={!!imageReference}
                onDeletePress={handleDeletePress}
              >
                <Button variant="secondary" onClick={handleCancel}>
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  onClick={handleSave}
                  disabled={!fieldsChanged}
                  submit
                  loading={saving}
                >
                  Save
                </Button>
              </EditCardControlsSection>
            </ButtonsSection>
          </StickyBottom>
        )}
      </div>
    </EditCardPopover>
  );
};
